import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus, Flame, Zap, Wheat, Droplet, Sparkles, ChevronDown } from "lucide-react";
import api from "@/lib/api";

// Pro-look "This week vs last" — v3, Whoop / Apple-Fitness inspired.
// Each metric gets a row: metric icon + label · hero delta · dual bar
// (last-week muted rail behind this-week accent fill). The dual bar reads
// "am I above / below last week's pace?" in a single glance without the
// athlete parsing numbers. Header carries a summary chip (X of 4 metrics
// tracking up) so athletes see momentum at the top-line before drilling in.

const METRICS = [
  { key: "kcal",    label: "Kcal",    icon: Flame,   accent: "#E2E30A", suffix: "" },
  { key: "carb",    label: "Carbs",   icon: Wheat,   accent: "#34C759", suffix: "g" },
  { key: "protein", label: "Protein", icon: Zap,     accent: "#4C9AFF", suffix: "g" },
  { key: "fat",     label: "Fat",     icon: Droplet, accent: "#FF9500", suffix: "g" },
];

function MetricRow({ label, icon: Icon, accent, thisVal, lastVal, suffix }) {
  const delta = (thisVal || 0) - (lastVal || 0);
  const pct = lastVal > 0 ? Math.round((delta / lastVal) * 100) : (thisVal > 0 ? 100 : 0);
  const trend = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const sign = delta > 0 ? "+" : delta < 0 ? "−" : "";
  // Normalise both bars against the greater of the two so proportions read
  // truthfully (Whoop-style — you can see at a glance "this week is 60% of
  // last week" or "this week is already 130% of last week").
  const denom = Math.max(thisVal || 0, lastVal || 0, 1);
  const thisPct = ((thisVal || 0) / denom) * 100;
  const lastPct = ((lastVal || 0) / denom) * 100;

  return (
    <div data-testid={`week-cmp-${label.toLowerCase()}`}
      className="grid grid-cols-[auto_1fr_auto] items-center gap-3 sm:gap-4 py-3 px-4 sm:px-5 border-b border-white/[0.05] last:border-b-0 hover:bg-white/[0.02] transition-colors">
      {/* Icon + label */}
      <div className="flex items-center gap-2 min-w-0 w-[86px] sm:w-[104px]">
        <span className="grid place-items-center h-7 w-7 shrink-0 border" style={{ borderColor: `${accent}55`, backgroundColor: `${accent}12` }}>
          <Icon className="h-3.5 w-3.5" style={{ color: accent }} />
        </span>
        <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-300 font-bold truncate">{label}</p>
      </div>

      {/* Dual bar — last-week muted rail + this-week accent fill overlay */}
      <div className="min-w-0">
        <div className="relative h-6">
          {/* Last week rail (muted) */}
          <div className="absolute inset-x-0 top-1 h-2 bg-white/5">
            <div className="h-full transition-[width] duration-700 ease-out"
              style={{ width: `${lastPct}%`, backgroundColor: `${accent}33` }} />
          </div>
          {/* This week fill (bold) */}
          <div className="absolute inset-x-0 bottom-1 h-2 bg-white/5">
            <div className="h-full transition-[width] duration-700 ease-out shadow-[0_0_8px_currentColor]"
              style={{ width: `${thisPct}%`, backgroundColor: accent, color: `${accent}66` }} />
          </div>
        </div>
        <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-wider mt-1">
          <span className="text-zinc-500">
            <span className="text-zinc-300 tabular-nums">{thisVal || 0}{suffix}</span> this
          </span>
          <span className="text-zinc-500">
            last <span className="text-zinc-400 tabular-nums">{lastVal || 0}{suffix}</span>
          </span>
        </div>
      </div>

      {/* Delta + % change */}
      <div className="text-right shrink-0 w-[72px] sm:w-[88px]">
        <div className="flex items-baseline justify-end gap-1">
          <TrendIcon className="h-3.5 w-3.5" style={{ color: trend === "flat" ? "#9CA3AF" : accent }} />
          <span className="font-display text-lg sm:text-xl leading-none tabular-nums" style={{ color: trend === "flat" ? "#9CA3AF" : accent }}>
            {trend === "flat" ? "±0" : `${sign}${Math.abs(delta)}`}
          </span>
        </div>
        <p className="text-[9px] font-mono uppercase tracking-wider text-zinc-500 mt-1 tabular-nums">
          {trend === "flat" ? "no change" : `${sign}${Math.abs(pct)}%`}
        </p>
      </div>
    </div>
  );
}

export default function WeekComparisonChip({ date }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Beta feedback (Feb 2026): "put these into a drop down that's missed
  // like the others" — matches Training / Meals which
  // collapse by default so the Tracker header stays lean. Athletes tap
  // to peek weekly deltas when they want them, otherwise the card is
  // just a one-line summary with the momentum chip.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(`/tracker/week-vs-last?date=${date}`);
        if (!cancelled) setData(d);
      } catch { /* silent — chip just hides */ }
      if (!cancelled) setLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [date]);

  if (!loaded) return null;
  if (!data || (data.this?.kcal === 0 && data.last?.kcal === 0)) return null;
  const last = data.last;

  // First tracked week — nothing to compare against yet.
  if (!last || last.kcal === 0) {
    return (
      <section data-testid="week-vs-last-chip"
        className="mt-4 relative overflow-hidden border border-white/10 bg-gradient-to-br from-[#141414] to-[#0A0A0A]">
        <button type="button" data-testid="week-vs-last-toggle" onClick={() => setOpen((o) => !o)}
          className="w-full flex items-center gap-4 px-4 sm:px-5 py-4 text-left hover:bg-white/[0.03] transition-colors">
          <span className="grid place-items-center h-12 w-12 shrink-0 bg-[#E2E30A]" style={{ boxShadow: "0 8px 24px -14px #E2E30A" }}>
            <Sparkles className="h-6 w-6 text-black" strokeWidth={2.2} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold uppercase tracking-[0.2em] text-white truncate">This week vs last</span>
            <span className="block text-xs text-zinc-300 mt-1 leading-snug">Calories, carbs, protein &amp; fat momentum</span>
          </span>
          <ChevronDown className={`h-4 w-4 text-zinc-300 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="px-4 sm:px-5 pb-4">
            <p className="text-xs text-zinc-400 leading-relaxed">
              <span className="text-[#E2E30A] font-semibold">First tracked week.</span> Comparisons unlock next Monday once we have a full week to weigh yours against.
            </p>
          </div>
        )}
      </section>
    );
  }

  // Momentum summary — count metrics tracking up vs down.
  let up = 0, down = 0;
  METRICS.forEach((m) => {
    const d = (data.this[m.key] || 0) - (data.last[m.key] || 0);
    if (d > 0) up += 1; else if (d < 0) down += 1;
  });
  const momentumLabel = up >= 3 ? "Trending up" : down >= 3 ? "Trending down" : "Mixed week";
  const momentumColour = up >= 3 ? "#34C759" : down >= 3 ? "#FF6A3D" : "#E2E30A";

  return (
    <section data-testid="week-vs-last-chip"
      className="mt-4 relative overflow-hidden border border-white/10 bg-gradient-to-br from-[#141414] to-[#0A0A0A]">
      {/* Ambient glow keyed off momentum so the whole card reads mood-first. */}
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-[0.06] blur-3xl pointer-events-none"
        style={{ backgroundColor: momentumColour }} />
      <button type="button" data-testid="week-vs-last-toggle" onClick={() => setOpen((o) => !o)}
        className="relative w-full flex items-center gap-4 px-4 sm:px-5 py-4 text-left hover:bg-white/[0.03] transition-colors">
        <span className="grid place-items-center h-12 w-12 shrink-0" style={{ backgroundColor: momentumColour, boxShadow: `0 8px 24px -14px ${momentumColour}` }}>
          <TrendingUp className="h-6 w-6 text-black" strokeWidth={2.2} />
        </span>
        <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold uppercase tracking-[0.2em] text-white truncate">This week vs last</span>
        <span className="block text-xs text-zinc-300 mt-1 leading-snug">{momentumLabel} · calories, carbs, protein &amp; fat</span>
      </span>
        <ChevronDown className={`h-4 w-4 text-zinc-300 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="relative border-t border-white/[0.06]">
          {METRICS.map((m) => (
            <MetricRow key={m.key} label={m.label} icon={m.icon} accent={m.accent}
              thisVal={data.this[m.key] || 0} lastVal={data.last[m.key] || 0} suffix={m.suffix} />
          ))}
        </div>
      )}
    </section>
  );
}
